import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Trash2, AlertTriangle } from 'lucide-react'
import { usePortfolioStore } from '../store/portfolioStore'
import { useTradingHistoryStore } from '../store/tradingHistoryStore'
import { formatCurrency, getStockTypeInfo } from '../lib/utils'
import Button from './ui/Button'
import Badge from './ui/Badge'

interface DeleteStockModalProps {
  isOpen: boolean
  onClose: () => void
}

const DeleteStockModal: React.FC<DeleteStockModalProps> = ({ isOpen, onClose }) => {
  const { 
    selectedStock, 
    deleteHolding,
    displayCurrency, 
    getCurrentPrice,
    convertPrice
  } = usePortfolioStore()
  
  const { deleteTradingRecordsBySymbol } = useTradingHistoryStore()
  
  const [loading, setLoading] = React.useState(false)

  if (!selectedStock) return null

  const typeInfo = getStockTypeInfo(selectedStock.port_api_type)
  const currentPrice = getCurrentPrice(selectedStock.symbol, displayCurrency)
  const purchasePriceInDisplayCurrency = convertPrice(
    selectedStock.purchase_price,
    selectedStock.purchase_currency,
    displayCurrency
  )
  const totalCost = purchasePriceInDisplayCurrency * selectedStock.shares
  const currentValue = currentPrice * selectedStock.shares

  const handleDelete = async () => {
    setLoading(true)
    try {
      // Delete the stock from portfolio
      await deleteHolding(selectedStock.symbol)
      
      // Delete all trading records for this symbol
      await deleteTradingRecordsBySymbol(selectedStock.symbol)
      
      onClose()
    } catch (error) {
      console.error('Error during deletion:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-loss" />
                Delete Stock
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Warning */}
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-loss mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
                    Permanent Deletion
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    This action cannot be undone. The stock will be completely removed from your portfolio 
                    and all associated trading records will also be permanently deleted.
                  </p>
                </div>
              </div>
            </div>

            {/* Stock Info */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{typeInfo.flag}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {selectedStock.symbol}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedStock.name}
                    </p>
                  </div>
                </div>
                <Badge 
                  variant="outline" 
                  size="sm"
                  className={`${typeInfo.bgColor} ${typeInfo.textColor} ${typeInfo.borderColor}`}
                >
                  {typeInfo.labelTh}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Holdings</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {selectedStock.shares.toLocaleString()} shares
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Total Cost</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(totalCost, displayCurrency)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Current Value</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(currentValue, displayCurrency)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Purchase Currency</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {selectedStock.purchase_currency}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1"
                type="button"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button variant="danger" className="flex-1"
                loading={loading}
                onClick={handleDelete}
              >
                Delete Stock
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default DeleteStockModal